import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { Megaphone, Link as LinkIcon, Tag } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", slug: "", starts_on: "", ends_on: "", blurb: "", banner_enabled: true };

// Phase 18-A2 item 3: admin campaign CRUD. One active max — activate is an
// explicit action that deactivates the rest in the same call.
export function CampaignsPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [tagId, setTagId] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => { api.adminCampaigns().then(setItems).catch(() => {}); };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.createCampaign(form);
      setForm(EMPTY);
      toast.success("Campaign created — activate it when you're ready");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not create"); }
    setBusy(false);
  };

  const act = async (c, fn, msg) => {
    try { await fn(c.id); toast.success(msg); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const tagEvent = async (c) => {
    if (!tagId.trim()) return;
    const kind = tagId.trim().startsWith("gath") ? "gathering" : "opportunity";
    try {
      await api.tagCampaignEvent(c.id, { event_type: kind, event_id: tagId.trim(), tagged: true });
      toast.success("Event tagged");
      setTagId("");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not tag — check the ID"); }
  };

  return (
    <div className="mt-14" data-testid="admin-campaigns">
      <h2 className="font-display text-2xl text-forest flex items-center gap-2">
        <Megaphone className="w-5 h-5 text-terracotta" /> Campaigns
      </h2>
      <p className="text-sm text-neutral-600 mt-1">One live at a time — activating a campaign switches any other off.</p>

      <form onSubmit={create} className="grid sm:grid-cols-2 gap-3 mt-4 max-w-2xl bg-white border border-neutral-200 rounded-xl p-5">
        <Input data-testid="campaign-form-name" required placeholder="Name (e.g. The Big Help Out)" value={form.name} onChange={set("name")} />
        <Input data-testid="campaign-form-slug" placeholder="Slug (optional)" value={form.slug} onChange={set("slug")} />
        <Input data-testid="campaign-form-starts" required type="date" value={form.starts_on} onChange={set("starts_on")} />
        <Input data-testid="campaign-form-ends" required type="date" value={form.ends_on} onChange={set("ends_on")} />
        <Textarea data-testid="campaign-form-blurb" rows={2} className="sm:col-span-2" placeholder="Blurb for the landing page and banner" value={form.blurb} onChange={set("blurb")} />
        <div className="sm:col-span-2 flex items-center justify-between">
          <label className="text-sm text-neutral-700 flex items-center gap-2 cursor-pointer">
            <input type="checkbox" data-testid="campaign-form-banner" checked={form.banner_enabled} onChange={(e) => setForm((f) => ({ ...f, banner_enabled: e.target.checked }))} />
            Site-wide banner while active
          </label>
          <Button data-testid="campaign-create-btn" type="submit" disabled={busy} className="rounded-full bg-forest hover:bg-forest-hover h-9 px-5 text-sm">Create campaign</Button>
        </div>
      </form>

      <div className="mt-5 space-y-3">
        {items.map((c) => (
          <div key={c.id} data-testid={`campaign-row-${c.id}`} className="bg-white border border-neutral-200 rounded-xl px-5 py-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <span className="font-medium text-forest">{c.name}</span>
                <span className="text-neutral-500 text-sm"> · {c.starts_on} → {c.ends_on}</span>
                <a href={`/campaigns/${c.slug}`} target="_blank" rel="noreferrer" className="ml-2 text-xs text-forest underline underline-offset-2 inline-flex items-center gap-1">
                  <LinkIcon className="w-3 h-3" /> /campaigns/{c.slug}
                </a>
              </div>
              <div className="flex items-center gap-2">
                {c.active ? (
                  <>
                    <Badge data-testid={`campaign-active-badge-${c.id}`} className="bg-forest text-white border-none rounded-full">Active</Badge>
                    <Button data-testid={`campaign-deactivate-${c.id}`} onClick={() => act(c, api.deactivateCampaign, "Deactivated")} variant="outline" className="rounded-full h-8 px-3 text-xs">Deactivate</Button>
                  </>
                ) : (
                  <Button data-testid={`campaign-activate-${c.id}`} onClick={() => act(c, api.activateCampaign, `${c.name} is live`)} className="rounded-full bg-forest hover:bg-forest-hover h-8 px-3 text-xs">Activate</Button>
                )}
                <Button data-testid={`campaign-delete-${c.id}`} onClick={() => { if (window.confirm(`Delete "${c.name}"? Tagged events will be untagged.`)) act(c, api.deleteCampaign, "Deleted (events untagged)"); }} variant="outline" className="rounded-full h-8 px-3 text-xs text-red-600 border-red-200 hover:bg-red-50">Delete</Button>
              </div>
            </div>
            {c.active && (
              <div className="flex items-center gap-2 mt-3">
                <Tag className="w-3.5 h-3.5 text-neutral-400" />
                <Input data-testid={`campaign-tag-input-${c.id}`} value={tagId} onChange={(e) => setTagId(e.target.value)} placeholder="Tag any event by ID (opp_… or gath_…)" className="h-8 text-xs max-w-xs" />
                <Button data-testid={`campaign-tag-btn-${c.id}`} onClick={() => tagEvent(c)} variant="outline" className="rounded-full h-8 px-3 text-xs">Tag</Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
